import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { map, catchError, of } from 'rxjs';
import { PaymentService } from '../service/payment.service';

/**
 * Guard that checks if the organization has an active subscription.
 * Redirects to /subscription-inactive if service_active is false.
 * Allows through if the status was already fetched and is active.
 * On API failure, redirects to /server-error to avoid confusing server issues with unpaid access.
 */
export const paymentGuard: CanActivateChildFn = () => {
  const paymentService = inject(PaymentService);
  const router = inject(Router);

  const status = paymentService.serviceStatus();

  if (status !== null) {
    return status.service_active ? true : router.createUrlTree(['/subscription-inactive']);
  }

  return paymentService.updateServiceStatus().pipe(
    map((result) =>
      result.service_active ? true : router.createUrlTree(['/subscription-inactive']),
    ),
    catchError((error) => {
      console.error('Payment status check failed, redirecting to server error page:', error);
      return of(router.createUrlTree(['/server-error']));
    }),
  );
};

/**
 * Guard for the /subscription-inactive route.
 * Only allows access if the subscription is actually inactive.
 * Redirects to /home if the subscription is active.
 * If the status cannot be verified, keeps the user on the inactive subscription page.
 */
export const inactiveSubscriptionGuard: CanActivateFn = () => {
  const paymentService = inject(PaymentService);
  const router = inject(Router);

  const status = paymentService.serviceStatus();

  if (status !== null) {
    return status.service_active ? router.createUrlTree(['/home']) : true;
  }

  return paymentService.updateServiceStatus().pipe(
    map((result) => (result.service_active ? router.createUrlTree(['/home']) : true)),
    catchError((error) => {
      console.error(
        'Payment status check failed while guarding inactive subscription page, allowing access:',
        error,
      );
      return of(true);
    }),
  );
};
